import React, { useCallback, useContext, useEffect, useState } from 'react'
import Programa from '../../../components/Programa/Programa'
import {
  Grid,
  Stepper,
  Step,
  StepLabel,
  TextField,
  Button,
  Typography,
  Checkbox,
  FormControlLabel,
  Dialog,
  DialogTitle,
  DialogContent,
} from '@mui/material'
import { metodoPrograma } from '../../../components/api'
import config from '../../../config.json'
import { GlobalContext } from '../../../App'
import Questionario from '../../questionario/componente/Questionario'
import { FaCheckCircle } from 'react-icons/fa'
import { setWarning } from '../../../components/alerts'

const TermoConsentimentoPublico = () => {
  const { ip } = useContext(GlobalContext)
  const [activeStep, setActiveStep] = useState(0)
  const [listaTermosUsuario, setListaTermosUsuario] = useState([])
  const [termosAceitos, setTermosAceitos] = useState({})
  const [temTermografia, setTemTermografia] = useState(false)

  const [cpf, setCpf] = useState('')
  const [dataNascimento, setDataNascimento] = useState('')
  const [dadosAtendimento, setDadosAtendimento] = useState({})

  const [loading, setLoading] = useState(false)

  const [usuarioValidado, setUsuarioValidado] = useState(false)

  const [modalTermo, setModalTermo] = useState(null)
  const [nomePaciente, setNomePaciente] = useState('')

  const steps = [
    'Identificação e Termos',
    'Anamnese Clínica',
    'Entrevista Psicológica',
    ...(temTermografia ? ['Termografia'] : []),
    'Confirmação',
  ]

  const getStepKey = (step) => {
    const baseSteps = [
      'termos',
      'anamnese',
      'psico',
      ...(temTermografia ? ['termografia'] : []),
      'confirmacao',
    ]

    return baseSteps[step]
  }
  const [anamnese, setAnamnese] = useState({
    questionario: { idQuestionario: 1 },
    resposta: {},
  })

  const [entrevistaPsico, setEntrevistaPsico] = useState({
    questionario: { idQuestionario: 2 },
    resposta: {},
  })

  const [termografia, setTermografia] = useState({
    questionario: { idQuestionario: 5 },
    resposta: {},
  })

  const handleNext = () => setActiveStep((prev) => prev + 1)

  const iniciar = async () => {
    if (!cpf || !dataNascimento) {
      setWarning('Informe CPF e Data de Nascimento')
      return
    }

    setLoading(true)

    const dados = {
      cdCpf: cpf,
      dtNascimento: dataNascimento,
    }

    const res = await metodoPrograma('validaUsuario', dados)

    if (res.status) {
      setDadosAtendimento(res)
      setNomePaciente(res.nmPaciente)
      setTemTermografia(res.termografia)
      setUsuarioValidado(true)
      getListaTermosUsuario()
    }

    setLoading(false)
  }

  const alterarTermo = (idTermo) => {
    setTermosAceitos((prev) => ({
      ...prev,
      [idTermo]: !prev[idTermo],
    }))
  }

  const getListaTermosUsuario = useCallback(() => {
    metodoPrograma('listaTermosUsuario', {
      cdCpf: cpf,
      idAtendimento: dadosAtendimento?.idAtendimento ?? undefined,
    }).then((res) => {
      if (res.status) {
        setListaTermosUsuario(res.lista)
      }
    })
  }, [cpf, dadosAtendimento])

  const confirmaTermosPessoa = useCallback(() => {
    const browserInfo = getBrowserInfo()
    metodoPrograma('confirmaTermosPessoa', {
      cdCpf: cpf,
      info: JSON.stringify(browserInfo),
      idAtendimento: dadosAtendimento.idAtendimento,
      termos: listaTermosUsuario,
    }).then((res) => {
      if (res.status) {
        handleNext()
      }
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cpf, dadosAtendimento, listaTermosUsuario])

  const getBrowserInfo = useCallback(() => {
    return {
      userAgent: navigator.userAgent,
      language: navigator.language,
      platform: navigator.platform,
      cookiesEnabled: navigator.cookieEnabled,
      screenResolution: `${window.screen.width}x${window.screen.height}`,
      timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      url: window.location.href,
      ip,
    }
  }, [ip])

  const handleFechar = () => {
    window.location.href = 'https://tourlife.com.br'
  }

  const todosAceitos =
    listaTermosUsuario.length > 0 &&
    listaTermosUsuario.every((termo) => termosAceitos[termo[0]])

  const buscaRespostaQuestionarioAnamnese = useCallback(async () => {
    const res = await metodoPrograma('getRespostaQuestionarioAtendimento', {
      idQuestionario: anamnese.questionario.idQuestionario,
      idAtendimento: dadosAtendimento?.idAtendimento ?? undefined,
      idPaciente: dadosAtendimento?.idPaciente,
    })
    setAnamnese({
      questionario: res.questionario,
      resposta: res.resposta,
    })
  }, [anamnese?.questionario?.idQuestionario, dadosAtendimento])

  const salvaRespostaQuestionarioAtendimentoAnamnese = useCallback(async () => {
    const browserInfo = getBrowserInfo()
    await metodoPrograma('salvaRespostaQuestionarioAtendimento', {
      idQuestionario: anamnese?.questionario?.idQuestionario,
      idAtendimento: dadosAtendimento?.idAtendimento ?? undefined,
      idResposta: anamnese?.resposta?.idResposta,
      idPaciente: dadosAtendimento?.idPaciente,
      info: JSON.stringify(browserInfo),
    })
  }, [
    anamnese?.questionario?.idQuestionario,
    anamnese?.resposta?.idResposta,
    dadosAtendimento?.idAtendimento,
    dadosAtendimento?.idPaciente,
    getBrowserInfo,
  ])

  const salvaRespostaQuestionarioAtendimentoPsico = useCallback(async () => {
    const browserInfo = getBrowserInfo()
    await metodoPrograma('salvaRespostaQuestionarioAtendimento', {
      idQuestionario: entrevistaPsico?.questionario?.idQuestionario,
      idAtendimento: dadosAtendimento?.idAtendimento ?? undefined,
      idResposta: entrevistaPsico?.resposta?.idResposta,
      idPaciente: dadosAtendimento?.idPaciente,
      info: JSON.stringify(browserInfo),
    })
  }, [
    entrevistaPsico?.questionario?.idQuestionario,
    entrevistaPsico?.resposta?.idResposta,
    dadosAtendimento?.idAtendimento,
    dadosAtendimento?.idPaciente,
    getBrowserInfo,
  ])

  const salvaRespostaQuestionarioAtendimentoTermo = useCallback(async () => {
    const browserInfo = getBrowserInfo()
    await metodoPrograma('salvaRespostaQuestionarioAtendimento', {
      idQuestionario: termografia?.questionario?.idQuestionario,
      idAtendimento: dadosAtendimento?.idAtendimento ?? undefined,
      idResposta: termografia?.resposta?.idResposta,
      idPaciente: dadosAtendimento?.idPaciente,
      info: JSON.stringify(browserInfo),
    })
  }, [
    termografia?.questionario?.idQuestionario,
    termografia?.resposta?.idResposta,
    dadosAtendimento?.idAtendimento,
    dadosAtendimento?.idPaciente,
    getBrowserInfo,
  ])

  const buscaRespostaQuestionarioPsico = useCallback(async () => {
    const res = await metodoPrograma('getRespostaQuestionarioAtendimento', {
      idQuestionario: entrevistaPsico.questionario.idQuestionario,
      idAtendimento: dadosAtendimento?.idAtendimento ?? undefined,
    })
    setEntrevistaPsico({
      questionario: res.questionario,
      resposta: res.resposta,
    })
  }, [entrevistaPsico?.questionario?.idQuestionario, dadosAtendimento])

  useEffect(() => {
    if (usuarioValidado) {
      // buscaRespostaQuestionarioAnamnese()
      // buscaRespostaQuestionarioPsico()
    }
  }, [
    buscaRespostaQuestionarioAnamnese,
    buscaRespostaQuestionarioPsico,
    usuarioValidado,
  ])

  useEffect(() => {
    const idRespostaAnemanese = anamnese?.resposta?.idResposta
    const idRespostaPsico = entrevistaPsico?.resposta?.idResposta
    const idRespostaTermo = termografia?.resposta?.idResposta

    if (Number.isInteger(idRespostaAnemanese)) {
      salvaRespostaQuestionarioAtendimentoAnamnese()
    }
    if (Number.isInteger(idRespostaPsico)) {
      salvaRespostaQuestionarioAtendimentoPsico()
    }
    if (Number.isInteger(idRespostaTermo)) {
      salvaRespostaQuestionarioAtendimentoTermo()
    }
  }, [
    salvaRespostaQuestionarioAtendimentoAnamnese,
    salvaRespostaQuestionarioAtendimentoPsico,
    salvaRespostaQuestionarioAtendimentoTermo,
    anamnese?.resposta?.idResposta,
    entrevistaPsico?.resposta?.idResposta,
    termografia?.resposta?.idResposta,
  ])

  const renderStepContent = () => {
    const stepKey = getStepKey(activeStep)

    switch (stepKey) {
      case 'termos':
        return (
          <Grid container spacing={3} justifyContent="center">
            {!usuarioValidado && (
              <>
                <Grid item xs={12}>
                  <Typography
                    align="center"
                    style={{ marginTop: 30, marginBottom: 40 }}
                  >
                    Para começar, informe seu CPF e sua data de nascimento.
                    Assim poderemos localizar seu cadastro e apresentar os
                    termos necessários para continuidade.
                  </Typography>
                </Grid>

                <Grid item xs={12} md={4}>
                  <TextField
                    label="CPF"
                    variant="outlined"
                    fullWidth
                    size="small"
                    value={cpf}
                    onChange={(e) => setCpf(e.target.value)}
                  />
                </Grid>

                <Grid item xs={12} md={4}>
                  <TextField
                    label="Data de Nascimento"
                    type="date"
                    variant="outlined"
                    fullWidth
                    size="small"
                    InputLabelProps={{ shrink: true }}
                    value={dataNascimento}
                    onChange={(e) => setDataNascimento(e.target.value)}
                  />
                </Grid>

                <Grid item xs={8}>
                  <Grid container justifyContent="center">
                    <Button
                      style={{ marginTop: 20 }}
                      variant="contained"
                      color="primary"
                      fullWidth
                      onClick={iniciar}
                      disabled={loading}
                    >
                      {loading ? 'Validando...' : 'Iniciar'}
                    </Button>
                  </Grid>
                </Grid>
              </>
            )}

            {usuarioValidado && (
              <Grid item xs={12} md={8}>
                <Grid item xs={12} style={{ marginBottom: 40 }}>
                  <Typography
                    variant="h6"
                    align="center"
                    style={{
                      color: '#2e7d32',
                      fontWeight: 500,
                    }}
                  >
                    Bem-vindo(a), {nomePaciente}!
                  </Typography>

                  <Typography align="center" style={{ marginTop: 4 }}>
                    Identificamos seu cadastro. Leia e aceite os termos abaixo
                    para continuar.
                  </Typography>
                </Grid>

                {listaTermosUsuario.map((termo) => {
                  const id = termo[0]
                  const titulo = termo[1]

                  return (
                    <FormControlLabel
                      key={id}
                      style={{ display: 'block' }}
                      control={
                        <Checkbox
                          checked={!!termosAceitos[id]}
                          onChange={() => alterarTermo(id)}
                        />
                      }
                      label={
                        <span
                          style={{
                            cursor: 'pointer',
                            textDecoration: 'underline',
                          }}
                          onClick={() => setModalTermo(termo)}
                        >
                          Li e concordo com {titulo}.
                        </span>
                      }
                    />
                  )
                })}

                <Grid
                  container
                  justifyContent="center"
                  style={{ marginTop: 20 }}
                >
                  <Button
                    variant="contained"
                    color="primary"
                    disabled={!todosAceitos}
                    onClick={confirmaTermosPessoa}
                  >
                    Continuar
                  </Button>
                </Grid>
              </Grid>
            )}
          </Grid>
        )

      case 'anamnese':
        return (
          <Questionario
            questionario={anamnese}
            setQuestionario={setAnamnese}
            dsNome={dadosAtendimento.nmPaciente}
            idPaciente={dadosAtendimento.idPaciente}
            onSubmit={(res) => {
              if (res?.status) {
                handleNext()
                window.scrollTo({
                  top: 0,
                  behavior: 'smooth',
                })
              }
            }}
          />
        )

      case 'psico':
        return (
          <Questionario
            questionario={entrevistaPsico}
            setQuestionario={setEntrevistaPsico}
            dsNome={dadosAtendimento.nmPaciente}
            idPaciente={dadosAtendimento.idPaciente}
            onSubmit={(res) => {
              if (res?.status) {
                handleNext()
                window.scrollTo({
                  top: 0,
                  behavior: 'smooth',
                })
              }
            }}
          />
        )

      case 'termografia':
        return (
          <Questionario
            questionario={termografia}
            setQuestionario={setTermografia}
            dsNome={dadosAtendimento.nmPaciente}
            idPaciente={dadosAtendimento.idPaciente}
            onSubmit={(res) => {
              if (res?.status) {
                handleNext()
                window.scrollTo({
                  top: 0,
                  behavior: 'smooth',
                })
              }
            }}
          />
        )

      case 'confirmacao':
        return (
          <Grid
            container
            direction="column"
            alignItems="center"
            justifyContent="center"
            style={{ marginTop: 40 }}
          >
            <FaCheckCircle size={100} color="#2e7d32" />

            <Typography
              variant="h5"
              style={{ marginTop: 20, fontWeight: 500 }}
              align="center"
            >
              Tudo certo!
            </Typography>

            <Typography
              variant="body1"
              style={{ marginTop: 10, maxWidth: 500 }}
              align="center"
            >
              Suas informações foram enviadas com sucesso. Agradecemos por
              dedicar seu tempo para preencher os dados. Isso é muito importante
              para o seu atendimento.
            </Typography>

            <Typography
              variant="body2"
              style={{ marginTop: 20, color: '#666' }}
              align="center"
            >
              Caso necessário, nossa equipe entrará em contato.
            </Typography>
            <Button
              variant="contained"
              color="primary"
              style={{ marginTop: 30 }}
              onClick={handleFechar}
            >
              Conheça o SPA Tour Life
            </Button>
          </Grid>
        )

      default:
        return null
    }
  }

  const handleStepClick = (step) => {
    if (podeIrParaStep(step)) {
      setActiveStep(step)
    } else {
      setWarning('Você precisa completar as etapas anteriores.')
    }
  }

  const podeIrParaStep = (step) => {
    if (step === 0) return true

    if (step === 1) {
      return usuarioValidado && todosAceitos
    }

    const idRespostaAnemanese = anamnese?.resposta?.idResposta

    if (step === 2) {
      return (
        usuarioValidado && todosAceitos && Number.isInteger(idRespostaAnemanese)
      )
    }
    const idRespostaPsico = entrevistaPsico?.resposta?.idResposta

    if (step === 3) {
      return (
        usuarioValidado && todosAceitos && Number.isInteger(idRespostaPsico)
      )
    }

    return false
  }

  return (
    <Programa codigo="CtrlTermoConsentimentoPublico" habTrocaLocal={false}>
      <Grid container spacing={4}>
        <Grid item xs={12}>
          <Grid container justifyContent="center">
            <img
              src={config.imgSistema}
              alt={`Logo ${config.titulo}`}
              style={{ maxWidth: 300, marginTop: 10 }}
            />
          </Grid>
        </Grid>

        <Grid item xs={12}>
          <Stepper activeStep={activeStep} alternativeLabel>
            {steps.map((label, index) => (
              <Step key={label}>
                <StepLabel
                  onClick={() => handleStepClick(index)}
                  style={{ cursor: 'pointer' }}
                >
                  {label}
                </StepLabel>
              </Step>
            ))}
          </Stepper>
        </Grid>

        <Grid item xs={12}>
          {renderStepContent()}
        </Grid>
      </Grid>

      <Dialog
        open={!!modalTermo}
        onClose={() => setModalTermo(null)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>{modalTermo ? modalTermo[1] : ''}</DialogTitle>

        <DialogContent>
          <Typography style={{ whiteSpace: 'pre-line' }}>
            {modalTermo ? modalTermo[2] : ''}
          </Typography>
        </DialogContent>
      </Dialog>
    </Programa>
  )
}

export default TermoConsentimentoPublico
